<template>
  <div>
    <transition name="fade">
      <div v-if="filteredSlides.length">
        <div class="slider-overlay"/>
        <div class="property-slider">
          <div class="property-slider__slides touch" :class="{ 'finite': filteredSlides.length <= 5 }">
            <vue-slick-carousel
              v-bind="settings"
              :key="`slickKey${filteredSlides.length ? filteredSlides.length + filteredSlides[0]._id : 0}`"
              @init="onInitCarousel"
              @afterChange="sliderChanged"
            >
              <template #prevArrow>
                <div class="property-slider__control property-slider__control--prev">
                  <button class="btn-circle touch">
                    <fa :icon="['far', 'chevron-left']" />
                  </button>
                </div>
              </template>

              <div
                v-for="slide in filteredSlides"
                class="property-slider__slide touch"
                :class="{ 'property-slider__slide--active' : activeSlideId === slide._id }"
                :key="slide._id"
              >
                <PropertyCard
                  :slide="slide"
                  :isActive="activeSlideId === slide._id"
                />
              </div>

              <template #nextArrow>
                <div class="property-slider__control property-slider__control--next">
                  <button class="btn-circle touch">
                    <fa :icon="['far', 'chevron-right']" />
                  </button>
                </div>
              </template>
              <template #customPaging="page">
                <div class="dot">
                  {{ page }}
                </div>
              </template>
            </vue-slick-carousel>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import PropertyCard from '@/components/PropertyCard'
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import { bus } from '../main'

const normalizeFilterValue = value => {
  if (typeof value === 'undefined' || value === null) return ''
  return value.toString().trim().toLowerCase()
}

const matchesMultiSelectFilter = (value, selectedValues) => {
  if (!Array.isArray(selectedValues) || selectedValues.includes(false)) return true

  const normalizedValue = normalizeFilterValue(value)
  return selectedValues.some(selectedValue => normalizeFilterValue(selectedValue) === normalizedValue)
}

const matchesSingleSelectFilter = (value, selectedValue) => {
  if (!selectedValue) return true

  const values = value && typeof value === 'object' ? Object.values(value) : [value]
  const normalizedSelectedValue = normalizeFilterValue(selectedValue)

  return values.some(candidate => normalizeFilterValue(candidate) === normalizedSelectedValue)
}

const matchesRangeFilter = (value, selectedRange) => {
  if (!Array.isArray(selectedRange) || selectedRange.length < 2 || Number(selectedRange[1]) === 0) return true

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return false

  return numericValue >= Number(selectedRange[0]) && numericValue <= Number(selectedRange[1])
}

export default {
  props: ['slides'],

  components: { PropertyCard, VueSlickCarousel },

  data () {
    return {
      activeSlideId: null,
      slidesPerPage: 5,

      settings: {
        "centerMode": true,
        "centerPadding": "0px",
        "dots": true,
        "focusOnSelect": true,
        "infinite": true,
        "speed": 500,
        "slidesToShow": 5,
        "slidesToScroll": 1,
        "swipeToSlide": true,
        "touchThreshold": 5
      }
    }
  },

  computed: {
    filteredSlides () {
      if (!this.$route.path.includes('/product/carousel')) return this.slides

      const filter = this.$store.state.filter

      return this.slides.filter(slide => {
        if (!matchesMultiSelectFilter(slide.productType, filter.inputType)) return false
        if (!matchesSingleSelectFilter(slide.viewLabel, filter.inputView)) return false
        if (!matchesSingleSelectFilter(slide.status, filter.inputStatus)) return false
        if (!matchesSingleSelectFilter(slide.name, filter.inputDisplayName)) return false
        if (!matchesMultiSelectFilter(slide.beds, filter.inputBeds)) return false
        if (!matchesMultiSelectFilter(slide.baths, filter.inputBaths)) return false
        if (!matchesMultiSelectFilter(slide.cars, filter.inputCars)) return false
        if (!matchesRangeFilter(slide.lotSize, filter.inputLotSize)) return false
        if (!matchesRangeFilter(slide.price, filter.inputLotPrice)) return false
        if (!matchesRangeFilter(slide.dwellingSize, filter.inputDwellingSize)) return false
        if (!matchesRangeFilter(slide.price, filter.inputDwellingPrice)) return false

        return true
      })
    },

    favourites () {
      return this.$store.getters['favourites/favourites']
    },

    language () {
      return this.$store.getters.language
    }
  },

  methods: {
    onInitCarousel () {
      this.setActiveSlideId(this.filteredSlides[0]._id)
    },

    setActiveSlideId (id) {
      this.$store.commit('SET_ACTIVE_PACKAGE_SLIDE_ID', id)

      setTimeout(() => {
        bus.$emit('startAnimations')
      }, 500)

      this.activeSlideId = id
    },

    sliderChanged (index) {
      this.setActiveSlideId(this.filteredSlides[index]._id, 0)
    }
  },
}
</script>

<style lang="scss">
$transition-speed: 300ms;
.slider-overlay {
  background: rgba(0, 0, 0, .3);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 0;
}

.property-slider {
  bottom: 16rem;
  left: calc(50% - 16.25rem);
  position: absolute;
  transform: translateX(-50%);
  z-index: var(--property-card-z-index);

  .slick-list {
    // height: 70rem;
    padding: 20rem 0 2rem !important;

    .finite & {
      overflow: visible;
    }
  }

  .slick-track {
    align-items: flex-end;
    display: flex;
  }

  .slick-slide {
    padding-left: 1rem;
    padding-right: 1rem;
    transform: scale(1);
    transform-origin: bottom;
    transition: transform 300ms ease-in-out;

    &.slick-center {
      transform: scale(1.25);
    }
  }

  .slick-arrow {
    bottom: 1rem;
    pointer-events: auto !important;
    position: absolute;
  }

  .slick-dots {
    align-items: center;
    bottom: -4rem;
    display: flex !important;
    justify-content: center;
    height: 6rem;
    margin: 0;
    padding: 0;
    position: absolute;
    width: 100%;

    li {
      background: transparent;
      font-size: 0;
      height: 1rem;
      list-style: none;
      margin: 0 .25rem;
      position: relative;
      width: 1rem;

      &.slick-active {
        .dot {
          background: #fff !important;
          border: .1rem solid #fff;
        }
      }

      .dot {
        background: transparent;
        border: .1rem solid rgba(#fff, .8);
        border-radius: 50%;
        height: 1rem;
        margin: 0;
        width: 1rem;
      }
    }
  }  

  &__slides {
    bottom: 3rem;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    width: 130rem;
  }
  
  &__slide {
    padding: 0 1rem;
  }

  &__control {
    position: absolute;

    &--prev {
      bottom: 4rem;
      left: -4rem;
    }

    &--next {
      bottom: 4rem;
      right: -4rem;
    }

    .btn-circle {
      background: #212122;
      color: #fff;
    }
  }
}
</style>
